/**
 * Datos de ejemplo para evaluar la app antes de usarla en serio.
 *
 * Genera guías repartidas en los últimos 60 días, con la mezcla real de
 * productos, clientes y plantas del histórico, más algunos trabajadores con
 * su jornada. Todo queda marcado como `esEjemplo` para poder borrarlo de una
 * sola pasada sin tocar los catálogos ni las guías reales.
 */

import { COL } from './firebase';
import { guardarLote, listar, actualizar } from './repositorio';
import { metaNueva, uuidv7, type DocGuia, type Meta } from './tiposDatos';
import type { DocCatalogo, DocPlanta, DocProducto } from './tiposDatos';
import { calcularTiempos } from '@/domain/tiempos';
import { calcularRecargos, totalRecargosUf } from '@/domain/recargos';
import { parsearProducto } from '@/domain/producto';
import { precioVigente } from '@/domain/precios';
import type { CodigoPlanta, ItemContrato, Turno } from '@/domain/tipos';

export const COL_TRABAJADORES = 'trabajadores';
export const COL_MARCAS = 'marcasAsistencia';

/** Marca que distingue lo generado de lo real. */
export interface ConEjemplo {
  esEjemplo?: boolean;
}

/** Generador reproducible: la misma semilla da siempre el mismo conjunto. */
function aleatorio(semilla: number) {
  let s = semilla;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

const elegir = <T>(xs: T[], r: () => number): T => xs[Math.floor(r() * xs.length)]!;

function fechaISO(diasAtras: number): string {
  const d = new Date();
  d.setDate(d.getDate() - diasAtras);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function instante(fecha: string, minutosDesdeMedianoche: number): Date {
  const [a, m, d] = fecha.split('-').map(Number);
  const dt = new Date(a!, m! - 1, d!, 0, 0, 0, 0);
  dt.setMinutes(minutosDesdeMedianoche);
  return dt;
}

export interface ResultadoEjemplo {
  guias: number;
  trabajadores: number;
  jornadas: number;
}

export async function cargarEjemplo(): Promise<ResultadoEjemplo> {
  const r = aleatorio(20260909);

  const [plantas, clientes, productos, equipos, personas, centros, destinos, items] =
    await Promise.all([
      listar<DocPlanta>(COL.plantas),
      listar<DocCatalogo>(COL.clientes),
      listar<DocProducto>(COL.productos),
      listar<DocCatalogo>(COL.equipos),
      listar<DocCatalogo>(COL.personas),
      listar<DocCatalogo>(COL.centrosCosto),
      listar<DocCatalogo>(COL.destinos),
      listar<ItemContrato>(COL.itemsContrato),
    ]);

  if (!plantas.length || !productos.length) {
    throw new Error('Faltan los catálogos base. Recarga la página e inténtalo de nuevo.');
  }

  const itemPorCodigo = new Map(items.map((i) => [i.codigo, i]));
  const buscarItem = (codigo: string) => {
    const it = itemPorCodigo.get(codigo);
    return it ? { ...it, precioUf: precioVigente(it) } : null;
  };

  // Partida principal según la planta.
  const itemDePlanta: Record<CodigoPlanta, string> = {
    'NV 17': '1.8',
    'KM 13': '2.8',
    VALLE: '3.24',
  };

  // Productos que sí generan recargos, para que los gráficos tengan variedad.
  const productosUtiles = productos.filter((p) => p.parseOk && p.generaRecargos).slice(0, 14);
  const usables = productosUtiles.length ? productosUtiles : productos.slice(0, 10);

  const guias: (DocGuia & ConEjemplo)[] = [];
  let folio = 900000;

  for (let dia = 59; dia >= 0; dia--) {
    const fecha = fechaISO(dia);
    const [aa, mm, dd] = fecha.split('-').map(Number);
    const diaSemana = new Date(aa!, mm! - 1, dd!).getDay();
    if (diaSemana === 0) continue; // domingo sin despacho

    const viajes = 2 + Math.floor(r() * 5);
    for (let v = 0; v < viajes; v++) {
      const planta = elegir(plantas, r);
      const producto = elegir(usables, r);
      const cantidad = elegir([4, 7, 7, 7, 6, 20, 3, 12, 15, 2.5], r);

      // Ciclo: salida entre 07:00 y 17:00, con esperas variables en obra.
      const salida = 420 + Math.floor(r() * 600);
      const ida = 10 + Math.floor(r() * 25);
      const espera = Math.floor(r() * 90);
      const descarga = 15 + Math.floor(r() * 60);
      const salidaObra = 3 + Math.floor(r() * 25);
      const vuelta = 10 + Math.floor(r() * 25);

      const t0 = salida;
      const t1 = t0 + ida;
      const t2 = t1 + espera;
      const t3 = t2 + descarga;
      const t4 = t3 + salidaObra;
      const t5 = t4 + vuelta;

      const marcas = {
        salidaPlanta: instante(fecha, t0),
        llegadaObra: instante(fecha, t1),
        inicioDescarga: instante(fecha, t2),
        terminoDescarga: instante(fecha, t3),
        salidaObra: instante(fecha, t4),
        llegadaPlanta: instante(fecha, t5),
      };

      const tiempos = calcularTiempos(marcas);
      const recargos = calcularRecargos(
        {
          plantaCodigo: planta.codigoPlanta,
          minCargaM3: planta.minCargaM3,
          cantidadM3: cantidad,
          producto: parsearProducto(producto.codigo),
          generaRecargos: producto.generaRecargos,
          tiempos,
          codigoProductoNormalizado: producto.codigoNormalizado,
        },
        buscarItem,
      );

      const cliente = elegir(clientes, r);
      const equipo = equipos.length ? elegir(equipos, r) : null;
      const opMixer = personas.length ? elegir(personas, r) : null;
      const opPlanta = personas.length ? elegir(personas, r) : null;
      const centro = centros.length ? elegir(centros, r) : null;
      const destino = destinos.length ? elegir(destinos, r) : null;
      const codigoItem = itemDePlanta[planta.codigoPlanta];
      const item = codigoItem ? buscarItem(codigoItem) : null;
      const turno: Turno = t0 < 720 ? 'A' : 'C';

      guias.push({
        ...metaNueva(null),
        esEjemplo: true,
        id: uuidv7(),
        plantaId: planta.id,
        plantaCodigo: planta.codigoPlanta,
        sector: planta.sector,
        folio: folio++,
        remito: null,
        fecha,
        turno,
        clienteId: cliente?.id ?? null,
        clienteNombre: cliente?.nombre ?? null,
        productoId: producto.id,
        productoCodigo: producto.codigo,
        cantidadM3: cantidad,
        // Los datos de ejemplo son hormigón del contrato.
        unidadCantidad: 'M3' as const,
        tipoVenta: 'CONTRATO' as const,
        epDeclarado: null,
        soloConsulta: false,
        centroCostoId: centro?.id ?? null,
        centroCostoCodigo: centro?.codigo ?? null,
        proyectoId: null,
        proyectoNombre: null,
        destinoId: destino?.id ?? null,
        destinoTexto: destino?.nombre ?? null,
        equipoId: equipo?.id ?? null,
        equipoCodigo: equipo?.codigo ?? null,
        operadorMixerId: opMixer?.id ?? null,
        operadorMixerNombre: opMixer?.nombre ?? null,
        operadorPlantaId: opPlanta?.id ?? null,
        operadorPlantaNombre: opPlanta?.nombre ?? null,
        solicitante: null,
        observaciones: null,
        tsSalidaPlanta: marcas.salidaPlanta.toISOString(),
        tsLlegadaObra: marcas.llegadaObra.toISOString(),
        tsInicioDescarga: marcas.inicioDescarga.toISOString(),
        tsTerminoDescarga: marcas.terminoDescarga.toISOString(),
        tsSalidaObra: marcas.salidaObra.toISOString(),
        tsLlegadaPlanta: marcas.llegadaPlanta.toISOString(),
        itemContratoId: item?.id ?? null,
        itemContratoCodigo: item?.codigo ?? null,
        estado: 'EMITIDA',
        motivoAnulacion: null,
        estadoPagoId: null,
        origen: 'APP',
        minEstadiaObra: tiempos.estadiaObra,
        minSobreEstadia: tiempos.sobreEstadia,
        bloquesCodelco: tiempos.bloquesCodelco,
        tiemposCompletos: tiempos.completa,
        montoRecargosUf: totalRecargosUf(recargos),
      });
    }
  }

  await guardarLote(COL.guias, guias);

  // ── Trabajadores y jornadas ─────────────────────────────────────────────
  const gente = [
    { rut: '12345678-5', nombre: 'CLAUDIO VICENCIO', cargo: 'Operador mixer', sueldo: 950000 },
    { rut: '9876543-3', nombre: 'JUAN URTUBIA', cargo: 'Operador planta', sueldo: 1100000 },
    { rut: '15784236-2', nombre: 'SERGIO GONZALEZ', cargo: 'Operador mixer', sueldo: 980000 },
    { rut: '17654321-0', nombre: 'HECTOR LAZCANO', cargo: 'Operador mixer', sueldo: 940000 },
  ];

  const trabajadores = gente.map((g) => ({
    ...metaNueva(null),
    esEjemplo: true,
    id: uuidv7(),
    rut: g.rut,
    nombre: g.nombre,
    cargo: g.cargo,
    jornadaId: 'ordinaria-42',
    sueldoBaseMensual: g.sueldo,
    activo: true,
  }));
  await guardarLote(COL_TRABAJADORES, trabajadores);

  // Turnos: la mayoría normales, algunos con extras y unos pocos fuera de norma.
  const turnos: [string, string, string, string][] = [
    ['08:00', '13:00', '14:00', '17:24'],
    ['08:00', '13:00', '14:00', '17:24'],
    ['08:00', '13:00', '14:00', '18:00'],
    ['07:30', '13:00', '14:00', '17:00'],
    ['07:00', '13:00', '14:00', '19:30'],
    ['08:30', '13:00', '14:00', '18:30'],
  ];

  interface DocMarcaEjemplo extends Meta, ConEjemplo {
    id: string;
    trabajadorId: string;
    fecha: string;
    entrada: string;
    inicioColacion: string;
    finColacion: string;
    salida: string;
    observacion: string | null;
  }
  const marcasJornada: DocMarcaEjemplo[] = [];
  for (let dia = 29; dia >= 0; dia--) {
    const fecha = fechaISO(dia);
    const [aa, mm, dd] = fecha.split('-').map(Number);
    if (new Date(aa!, mm! - 1, dd!).getDay() === 0) continue;
    for (const t of trabajadores) {
      const turno = elegir(turnos, r);
      marcasJornada.push({
        ...metaNueva(null),
        esEjemplo: true,
        id: uuidv7(),
        trabajadorId: t.id,
        fecha,
        entrada: turno[0],
        inicioColacion: turno[1],
        finColacion: turno[2],
        salida: turno[3],
        observacion: null,
      });
    }
  }
  await guardarLote(COL_MARCAS, marcasJornada);

  return {
    guias: guias.length,
    trabajadores: trabajadores.length,
    jornadas: marcasJornada.length,
  };
}

/** Cuenta lo generado, para saber si hay algo que borrar. */
export async function contarEjemplo(): Promise<number> {
  const [g, t, m] = await Promise.all([
    listar<DocGuia & ConEjemplo>(COL.guias),
    listar<Meta & ConEjemplo>(COL_TRABAJADORES),
    listar<Meta & ConEjemplo>(COL_MARCAS),
  ]);
  const vivos = <T extends Meta & ConEjemplo>(xs: T[]) =>
    xs.filter((x) => x.esEjemplo && !x.eliminadoEn).length;
  return vivos(g) + vivos(t) + vivos(m);
}

/** Borra sólo lo generado. Las guías cargadas a mano quedan intactas. */
export async function borrarEjemplo(): Promise<number> {
  const ahora = new Date().toISOString();
  let borrados = 0;

  for (const coleccion of [COL.guias, COL_TRABAJADORES, COL_MARCAS]) {
    const docs = await listar<{ id: string } & ConEjemplo & Meta>(coleccion);
    for (const d of docs.filter((x) => x.esEjemplo && !x.eliminadoEn)) {
      await actualizar(coleccion, d.id, { eliminadoEn: ahora }, null);
      borrados++;
    }
  }
  return borrados;
}
